import { Box, TextField, Typography } from '@mui/material';
import { FC, useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import Routes, { DELIVERY_ID_PARAM, SET_ID_PARAM, TRAY_ID_PARAM } from 'routes';
import { useLastDmInspection } from 'api/hooks/useLastDmInspection';
import { Delivery } from 'api/models/Deliveries';
import theme from 'App.theme';
import Button from 'components/Button';
import Card from 'components/Card/Card';
import MissingDevicesReasonModal from 'components/CheckInOut/components/MissingDevicesReasonModal';
import ConsolidatedMembersPanel from 'components/ConsolidatedMembersPanel';
import PreImageCaptureInspection from 'components/CheckInOut/components/PreImageCaptureInspection';
import TrayImage from 'components/CheckInOut/components/TrayImage';
import { Switch } from 'components/Switch/Switch';
import { useLanguage } from 'context/AppContext';
import { useCheckInTraysBySet } from 'hooks/useCheckInTraysBySet';
import { useDownloadUrlFromGS } from 'hooks/useDownloadUrlFromGS';
import { useSetManuals } from 'hooks/useSetManuals';
import { SetManualsModal } from './SetManualsModal';

type TCheckInTrayDetails = FC<{
  delivery?: Delivery;
  readonly?: boolean;
  allDevicesCleanOfDebris?: boolean;
  setAllDevicesCleanOfDebris?: (val: boolean) => void;
  allDevicesFunctional?: boolean;
  setAllDevicesFunctional?: (val: boolean) => void;
  allDevicesInTray?: boolean;
  setMissingPartsComment?: (reason: string) => void;
  setAllDevicesInTray?: (val: boolean) => void;
  comments?: string;
  setComments?: (val: string) => void;
}>;

export const CheckInTrayDetails: TCheckInTrayDetails = ({
  delivery,
  readonly = false,
  allDevicesCleanOfDebris,
  setAllDevicesCleanOfDebris,
  allDevicesFunctional,
  setAllDevicesFunctional,
  allDevicesInTray,
  setMissingPartsComment,
  setAllDevicesInTray,
  comments,
  setComments,
}) => {
  const lang = useLanguage()[0].CheckInTrayDetailsPage;
  const { deliveryId = '', setId: setKitInstanceId = '', checkInTrayId = '', trayId = '' } = useParams();

  const [checkInTraysBySet] = useCheckInTraysBySet();
  const checkInTray = checkInTraysBySet[setKitInstanceId]?.trays[checkInTrayId];

  const [devicesMissingModalOpen, setDevicesMissingModalOpen] = useState(false);
  const [setManualsModalOpen, setSetManualsModalOpen] = useState(false);

  // we need to revisit the way we manage data in checkin, for
  //  sadl sets we save the tray and thus have a trayInstanceId, for non
  //  sadl sets we dont have a trayInstanceId unless passed in trayId
  const trayInstanceIdIfNotCheckinOrIsSadlSet = trayId || checkInTray?.trayInstanceId || '';

  const [deliverySet, deliveryTray, deliveryInspection] = useMemo(() => {
    const deliverySet = delivery?.sets.find(set => set.kitInstanceId == setKitInstanceId);
    const deliveryTray = deliverySet?.trays.find(tray => tray.trayInstanceId == trayInstanceIdIfNotCheckinOrIsSadlSet);
    const deliveryInspection = deliveryTray?.inspections.find(inspection => inspection.stage == 'CHECK_IN');
    return [deliverySet, deliveryTray, deliveryInspection];
  }, [delivery?.sets, setKitInstanceId, trayInstanceIdIfNotCheckinOrIsSadlSet]);
  const isSadlSet = deliverySet?.isSadlSet;

  const consolidatedMembers = useMemo(
    () =>
      deliverySet?.consolidatedSet
        ? (delivery?.sets || []).filter(set => set.consolidatedIntoKitInstanceId == deliverySet.kitInstanceId)
        : [],
    [delivery?.sets, deliverySet],
  );

  const { data: trayImageUrl } = useDownloadUrlFromGS({
    storagePath: deliveryInspection?.gsImagePath || '',
  });

  const { data: lastDmInspection } = useLastDmInspection({
    setId: setKitInstanceId,
    trayInstanceId: trayInstanceIdIfNotCheckinOrIsSadlSet,
  });

  const manualRows = useSetManuals({ deliverySet, supplierTenantName: delivery?.supplier.name });
  const haveManuals = !!manualRows.length;

  const navigate = useNavigate();

  const onViewStandardPictureList = useCallback(() => {
    // if there is no trayInstanceId, then sadl set's aren't being loaded correctly
    if (!trayInstanceIdIfNotCheckinOrIsSadlSet) {
      throw new Error('No tray instance id found');
    }

    navigate(
      Routes.TrayPictureList.path
        .replace(DELIVERY_ID_PARAM, deliveryId)
        .replace(SET_ID_PARAM, setKitInstanceId)
        .replace(TRAY_ID_PARAM, trayInstanceIdIfNotCheckinOrIsSadlSet),
    );
  }, [navigate, deliveryId, setKitInstanceId, trayInstanceIdIfNotCheckinOrIsSadlSet]);

  const onViewLastDmInspection = useCallback(() => {
    // if there is no trayInstanceId, then sadl set's aren't being loaded correctly
    if (!trayInstanceIdIfNotCheckinOrIsSadlSet) {
      throw new Error('No tray instance id found');
    }

    navigate(
      Routes.LastTrayInspection.path
        .replace(DELIVERY_ID_PARAM, deliveryId)
        .replace(SET_ID_PARAM, setKitInstanceId)
        .replace(TRAY_ID_PARAM, trayInstanceIdIfNotCheckinOrIsSadlSet),
    );
  }, [trayInstanceIdIfNotCheckinOrIsSadlSet, deliveryId, navigate, setKitInstanceId]);

  const onViewInstructions = useCallback(() => {
    setSetManualsModalOpen(true);
  }, []);

  const clickAllDevicesInTray = useCallback(() => {
    if (allDevicesInTray) {
      setAllDevicesInTray?.(false);
      setDevicesMissingModalOpen(true);
    } else {
      setAllDevicesInTray?.(true);
    }
  }, [allDevicesInTray, setAllDevicesInTray]);

  const trayImageSrc = (readonly && trayImageUrl) || checkInTray?.trayImage?.dataUri || '';

  const canShowStandardPictureList = !!(isSadlSet && deliverySet?.serial && deliveryTray?.trayCode);

  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <div
        style={{
          maxWidth: 480,
          width: '100%',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography variant="h3" component="h3">
          {lang.heading}
        </Typography>

        <TrayImage
          imageUrl={trayImageSrc}
          imageAlt={(readonly && deliveryTray?.name) || checkInTray?.name || ''}
          width="auto"
          height="250px"
        />

        <ConsolidatedMembersPanel members={consolidatedMembers} />

        <div
          style={{
            marginTop: theme.layout.spacing.small,
            display: 'flex',
            flexDirection: 'column',
            gap: theme.layout.spacing.medium,
          }}
        >
          {
            // we have ended up with code duplications around the inspections
            // we sould refactor them to be more reusable and maintainable
          }
          {!readonly ? (
            <>
              <div style={{ display: 'flex', flexDirection: 'row', gap: theme.layout.spacing.small }}>
                <Typography variant="subtitle2" sx={{ flex: 1 }}>
                  {lang.qualityCheck}
                </Typography>

                <Typography variant="subtitle2" sx={{ flex: 1 }}>
                  {lang.result}
                </Typography>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: theme.layout.spacing.small,
                }}
              >
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {lang.allDevicesCleanOfDebris}
                </Typography>

                <Switch
                  checkedText={lang.pass}
                  uncheckedText={lang.fail}
                  checked={!!allDevicesCleanOfDebris}
                  onChange={checked => setAllDevicesCleanOfDebris?.(checked)}
                  disabled={!setAllDevicesCleanOfDebris}
                />
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: theme.layout.spacing.small,
                }}
              >
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {lang.allDevicesFunctional}
                </Typography>

                <Switch
                  checkedText={lang.pass}
                  uncheckedText={lang.fail}
                  checked={!!allDevicesFunctional}
                  onChange={checked => setAllDevicesFunctional?.(checked)}
                  disabled={!setAllDevicesCleanOfDebris}
                />
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: theme.layout.spacing.small,
                }}
              >
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {lang.allDevicesInTray}
                </Typography>

                <Switch
                  checkedText={lang.pass}
                  uncheckedText={lang.fail}
                  checked={!!allDevicesInTray}
                  onChange={clickAllDevicesInTray}
                  disabled={!setAllDevicesInTray}
                />
              </div>
            </>
          ) : deliveryInspection ? (
            <PreImageCaptureInspection
              inspection={deliveryInspection}
              trayId={trayInstanceIdIfNotCheckinOrIsSadlSet}
              disabled={true}
            />
          ) : null}
        </div>

        <TextField
          sx={{ mt: theme.layout.spacing.medium }}
          label={lang.commentsLabel}
          placeholder={lang.commentsPlaceholder}
          multiline
          value={(readonly && deliveryInspection?.comments) || comments || ''}
          onChange={event => setComments?.(event.target.value)}
          disabled={readonly || !setComments}
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: theme.layout.spacing.medium,
            mt: theme.layout.spacing.medium,
          }}
        >
          {canShowStandardPictureList && (
            <Button sx={{ height: '36px', borderRadius: '4px' }} onClick={onViewStandardPictureList}>
              {lang.viewStandardPictureList}
            </Button>
          )}

          {lastDmInspection?.gsImagePath && lastDmInspection?.inspectionResults && (
            <Button sx={{ height: '36px', borderRadius: '4px' }} onClick={onViewLastDmInspection}>
              {lang.viewLastDmInspection}
            </Button>
          )}

          {haveManuals && (
            <Button sx={{ height: '36px', borderRadius: '4px' }} onClick={onViewInstructions}>
              {lang.viewInstructions}
            </Button>
          )}
        </Box>
      </div>
      <MissingDevicesReasonModal
        open={devicesMissingModalOpen}
        close={() => setDevicesMissingModalOpen(false)}
        handleConfirmMissingPartsReason={setMissingPartsComment || (() => {})}
        handleCancelMissingPartsReason={() => setAllDevicesInTray?.(true)}
      />
      <SetManualsModal open={setManualsModalOpen} close={() => setSetManualsModalOpen(false)} manualRows={manualRows} />
    </Card>
  );
};
